"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "@/helpers/Image";
import Link from "next/link";
import { IHeader } from "@/sanity/lib/definitions";
import { forwardUTMParams } from "@/lib/utm";
import { usePostHog } from "posthog-js/react";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";

import bitbullLogoImg from "@/public/lp2/global/bitbull-logo.svg";
import bitunixLogoImg from "@/public/lp2/global/logo-bitunix.svg";
import phemexLogoImg from "@/public/lp2/global/logo-phemex.svg";
import pionexLogoImg from "@/public/lp2/global/logo-pionex.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(useGSAP);
}

export default function Header({
  data,
  bitunix,
  phemex,
  pionex,
}: {
  data: IHeader;
  bitunix?: boolean;
  phemex?: boolean;
  pionex?: boolean;
}) {
  //
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const [loginUrl, setLoginUrl] = useState("https://trademania.app/login");
  const posthog = usePostHog();

  useEffect(() => {
    // Forward UTM parameters to login URL
    const urlWithUTMs = forwardUTMParams("https://trademania.app/login");
    setLoginUrl(urlWithUTMs);
  }, []);

  useGSAP(
    () => {
      gsap.set(containerRef.current, { visibility: "visible" });

      const navbarTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.5 },
      });

      navbarTL.from(containerRef.current, {
        y: "-0.5rem",
        delay: 1,
      });
    },
    { scope: containerRef }
  );

  if (!data) {
    return null;
  }

  return (
    <header
      id="navbar" // To set hidden initial state with global CSS
      ref={containerRef}
      className="w-full py-[3rem] lg:py-[4rem] absolute top-0 start-0 end-0 z-[15]"
    >
      <div className="container-lp mx-auto">
        <div className="flex justify-between items-center">
          <Link href={"/"}>
            <Image
              className={
                bitunix || phemex || pionex
                  ? "w-[30rem] lg:w-[40rem] min-w-[30rem] lg:min-w-[40rem] max-w-[30rem] lg:max-w-[40rem]"
                  : "w-[13rem] lg:w-[15rem] min-w-[13rem] lg:min-w-[15rem] max-w-[13rem] lg:max-w-[15rem]"
              }
              alt="Logo"
              src={
                bitunix
                  ? bitunixLogoImg
                  : phemex
                    ? phemexLogoImg
                    : pionex
                      ? pionexLogoImg
                      : bitbullLogoImg
              }
            />
          </Link>

          <a
            href={loginUrl}
            className="px-[3rem] py-[1rem] rounded-[4.5rem] border-[.25rem] border-white/20 bg-black/20 hover:bg-white/10 transition-colors backdrop-blur-[2rem] text-white/80 font-medium"
            onClick={() => {
              posthog?.capture('clicked_login_button', {
                source: 'header_lp',
                destination_url: loginUrl,
                affiliate: localStorage.getItem('affiliate') || undefined,
              });
            }}
          >
            Login
          </a>
        </div>
      </div>
    </header>
  );
}
